import { useCallback, useContext } from 'react';
import _ from 'lodash';

import MetricOptionsContext from 'contexts/MetricOptionsContext';
import isDefined from 'isDefined';
import metricTypeCheckers from 'types/metricTypeCheckers';
import useToMetricInput from 'hooks/useToMetricInput';

/**
 * The GQL request pieces a single metric contributes to an aggregation query.
 *
 * `ready` is false when the metric is a compound whose input metrics are not
 * all loaded yet. In that state the resolver deliberately returns NO
 * `expression`, because emitting the expression while an input is missing from
 * `metricInputs` leaves a raw child UUID in the expression string that the
 * backend cannot substitute — it rejects the request with
 * "parse error: Expected EOF". Callers must not fire a request while `ready`
 * is false.
 */
export interface ResolvedMetricRequest {
  metricInputs: MetricInput[];
  expression?: Expression2;
  special?: Metrics.SpecialMetric;
  ready: boolean;
}

export interface MetricsAndExpressions {
  metrics: MetricInput[];
  expressions: Expression2[];
  specials: Metrics.SpecialMetric[];
  ready: boolean;
}

interface ResolveOptions {
  // Threaded straight through to `toMetricInput` for the metric AND (for a
  // compound) its children. Metric List / Scorecard / Goal pass a per-item
  // flag; other consumers omit it and fall back to FilterPlatesContext. Getting
  // this wrong silently changes which default dataset filters are applied.
  isDefaultFiltersDisabled?: boolean;
}

/**
 * The single canonical "resolve metric(s) into an aggregation request" helper.
 *
 * Every visualisation fetch path should build its `metrics[]`/`expressions[]`
 * through this, rather than hand-rolling the compound-flattening + input
 * lookup (which is how the "Expected EOF" class of bug diverged across vis
 * types). It only produces the metric/expression/special arrays plus a
 * readiness flag — request-level concerns (filters, dateScope, comparison,
 * groupBy, result-id remapping, special-metric routing, etc.) stay with the
 * consumer.
 */
const useMetricAggregationRequest = () => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const toMetricInput = useToMetricInput();

  const resolveMetricForRequest = useCallback(
    (
      metric: Metrics.Metric,
      { isDefaultFiltersDisabled }: ResolveOptions = {},
    ): ResolvedMetricRequest => {
      if (metricTypeCheckers.isSpecialMetric(metric)) {
        return { metricInputs: [], special: metric, ready: true };
      }

      if (metricTypeCheckers.isCompoundMetric(metric)) {
        const inputs = metric.metricIds
          .map((id) => metricOptionsLookup[id])
          .filter(isDefined)
          .filter(metricTypeCheckers.isNormalMetric);

        // Only ready once every referenced input has resolved to a real normal
        // metric — otherwise we must not emit the expression (see above). The
        // length check runs on the raw (pre-dedupe) inputs so a compound that
        // lists the same input twice still reports ready correctly.
        if (inputs.length !== metric.metricIds.length) {
          return { metricInputs: [], ready: false };
        }

        return {
          // Dedupe so a compound with a repeated input id can't emit duplicate
          // MetricInputs (single-metric consumers use this array directly).
          metricInputs: _.uniqBy(
            inputs.map((m) => toMetricInput(m, isDefaultFiltersDisabled)),
            'id',
          ),
          expression: { id: metric.id, expression: metric.expression },
          ready: true,
        };
      }

      // normal / single-use
      return {
        metricInputs: [toMetricInput(metric, isDefaultFiltersDisabled)],
        ready: true,
      };
    },
    [metricOptionsLookup, toMetricInput],
  );

  const buildMetricsAndExpressions = useCallback(
    (
      metrics: Metrics.Metric[],
      opts: ResolveOptions = {},
    ): MetricsAndExpressions => {
      const resolved = metrics.map((m) => resolveMetricForRequest(m, opts));
      return {
        metrics: _.uniqBy(
          resolved.flatMap((r) => r.metricInputs),
          'id',
        ),
        expressions: _.uniqBy(
          resolved.map((r) => r.expression).filter(isDefined),
          'id',
        ),
        specials: _.uniqBy(
          resolved.map((r) => r.special).filter(isDefined),
          'id',
        ),
        ready: resolved.every((r) => r.ready),
      };
    },
    [resolveMetricForRequest],
  );

  return { resolveMetricForRequest, buildMetricsAndExpressions };
};

export default useMetricAggregationRequest;
